// MCHD Room Scheduler - Loading Screen Component
// Professional loading screen with MCHD branding and smooth animations

import { Box, Container, Text, Group, Stack } from '@mantine/core';
import { IconHospital, IconCalendar, IconShield } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

/**
 * Props for the LoadingScreen component
 */
interface LoadingScreenProps {
  message?: string;
  showProgress?: boolean;
  progress?: number;
}

/**
 * Professional loading screen with MCHD branding
 * 
 * @param message - Custom loading message
 * @param showProgress - Whether to show progress indicator
 * @param progress - Progress percentage (0-100)
 * @returns JSX element
 */
export function LoadingScreen({ 
  message = 'Loading MCHD Room Scheduler...', 
  showProgress = false,
  progress = 0 
}: LoadingScreenProps): JSX.Element {
  const [dots, setDots] = useState('');

  // Animate loading dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <Container size="sm">
        <Stack ta="center" gap="xl">
          {/* MCHD Logo and Branding */}
          <Group gap="md" ta="center">
            <Box
              style={{
                width: 60,
                height: 60,
                backgroundColor: '#1e3a8a',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(30, 58, 138, 0.3)',
              }}
            >
              <IconHospital size={32} color="white" />
            </Box>
            <Stack gap={4}>
              <Text
                size="xl"
                fw={700}
                color="#1e3a8a"
                style={{ fontFamily: 'Rubik, sans-serif' }}
              >
                MCHD Scheduler
              </Text>
              <Text
                size="sm"
                color="#64748b"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Hospital Room Scheduling System
              </Text>
            </Stack>
          </Group>

          {/* Loading Animation */}
          <Stack ta="center" gap="lg">
            {/* Spinner */}
            <Box
              style={{
                width: 48,
                height: 48,
                border: '4px solid #e2e8f0',
                borderTop: '4px solid #1e3a8a',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />

            {/* Loading Message */}
            <Text
              size="lg"
              fw={500}
              color="#475569"
              ta="center"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {message}{dots}
            </Text>

            {/* Progress Bar */}
            {showProgress && (
              <Box
                style={{
                  width: '100%',
                  maxWidth: 300,
                  height: 4,
                  backgroundColor: '#e2e8f0',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: '#1e3a8a',
                    borderRadius: 2,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            )}

            {/* Feature Icons */}
            <Group gap="xl" mt="xl">
              <Stack ta="center" gap="xs">
                <IconCalendar size={24} color="#1e3a8a" />
                <Text size="xs" color="#64748b" fw={500}>
                  Room Booking
                </Text>
              </Stack>
              <Stack ta="center" gap="xs">
                <IconShield size={24} color="#1e3a8a" />
                <Text size="xs" color="#64748b" fw={500}>
                  Secure Access
                </Text>
              </Stack>
              <Stack ta="center" gap="xs">
                <IconHospital size={24} color="#1e3a8a" />
                <Text size="xs" color="#64748b" fw={500}>
                  Hospital Grade
                </Text>
              </Stack>
            </Group>
          </Stack>
        </Stack>
      </Container>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}

/**
 * Error loading screen for authentication failures
 */
export function ErrorLoadingScreen({ 
  message = 'Authentication failed. Please try again.',
  onRetry 
}: { 
  message?: string; 
  onRetry?: () => void; 
}): JSX.Element {
  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fef2f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <Container size="sm">
        <Stack ta="center" gap="xl">
          {/* Error Icon */}
          <Box
            style={{
              width: 60,
              height: 60,
              backgroundColor: '#dc2626',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
            }}
          >
            <IconShield size={32} color="white" />
          </Box>

          {/* Error Message */}
          <Stack ta="center" gap="md">
            <Text
              size="xl"
              fw={700}
              color="#dc2626"
              ta="center"
              style={{ fontFamily: 'Rubik, sans-serif' }}
            >
              Authentication Error
            </Text>
            <Text
              size="lg"
              color="#7f1d1d"
              ta="center"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {message}
            </Text>
          </Stack>

          {/* Retry Button */}
          {onRetry && (
            <Box
              style={{
                padding: '12px 24px',
                backgroundColor: '#1e3a8a',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s ease',
              }}
              onClick={onRetry}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1e40af';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e3a8a';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Retry
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
